<script setup lang="ts">
import { ref, computed } from 'vue';
import FavoriteButton from '@components-molecules/FavoriteButton.vue';
import type { SelectionItem } from '@models/selectionItem.ts';
import UserAuth from '@tools/user/auth';
import { useLoading } from '@composables/useLoading.ts';
import IconBookmarkSimpleFill from '@icons/ph_bookmark-simple-fill.svg';
import Icon from '@components/Icon.vue';
import Button from '@components/Button.vue';
import useSelectionItemStore from '../stores/useSelectionItemStore';

const props = defineProps<{ selectionItem: SelectionItem }>();

const { setSelectionItemAsFavorite, archiveProposal } = useSelectionItemStore();

const { isLoading, startLoading, stopLoading } = useLoading();

const isActiveProposal = ref<boolean>(
  !props.selectionItem.is_removed && (props.selectionItem.proposal?.is_active ?? false),
);

const isFavorite = ref<boolean>(props.selectionItem.is_favorite);

const isImpersonated = computed<boolean>(() => UserAuth.isImpersonated());

const handleArchiveProposal = async (): Promise<void> => {
  startLoading();

  await archiveProposal({ ...props.selectionItem, is_favorite: isFavorite.value });

  isActiveProposal.value = false;

  stopLoading();
};

const changeFavorite = async (newIsFavorite: boolean): Promise<void> => {
  setSelectionItemAsFavorite({
    offerHashId: props.selectionItem.offer.hash_id,
    isRemoved: props.selectionItem.is_removed,
    newIsFavorite,
  });

  isFavorite.value = newIsFavorite;
};
</script>

<template>
  <div class="selection-item-tile-actions">
    <Button
      v-if="isActiveProposal && isImpersonated"
      variant="circle"
      class="selection-tile-action"
      :disabled="isLoading"
      @click.prevent="handleArchiveProposal"
    >
      <Icon
        :svg="IconBookmarkSimpleFill"
        width="20"
        height="20"
        fill="blue"
      />
    </Button>

    <FavoriteButton
      :offer-hash-id="selectionItem.offer.hash_id"
      :offer-team-sizes="selectionItem.offers"
      :is-active="isFavorite"
      @change="changeFavorite"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.selection-item-tile-actions {
  display: flex;
  gap: rem(10);
}

.selection-tile-action {
  width: rem(36);
  height: rem(36);
  box-shadow: 0 rem(2) rem(4) 0 rgba(0, 0, 0, 0.10);
}
</style>
